import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { getPendingReviews } from '@/api/widget'
import { hydratePendingReviewProduct, PendingReviewsResponse } from '@/types/widget'
import { getCurrentGlobalID, getCurrentMenuType, useSessionStore } from './useSessionStore'
import { logger, showErrorToast } from '@/utils'
import { track } from '@/utils/analytics'
import { classifyReviewError } from '@/utils/reviewErrors'
import { withRefreshRetry } from '@/utils/reviewAuth'
import { getStoreIDByGlobalID } from './useLocationsStore'

type PendingReviewsStoreState = {
  loading: boolean
  loaded: boolean
  pendingReviews: PendingReviewsResponse | undefined
  reviewsDisabled: boolean
  error: string | undefined
}

type PendingReviewsStoreActions = {
  initialize: () => Promise<void>
  fetch: (force?: boolean) => Promise<PendingReviewsResponse | undefined>
  reset: () => void
}

type PendingReviewsStore = PendingReviewsStoreState & PendingReviewsStoreActions

const initialState: PendingReviewsStoreState = {
  loading: false,
  loaded: false,
  pendingReviews: undefined,
  reviewsDisabled: false,
  error: undefined,
}

export const usePendingReviewsStore = create<PendingReviewsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetch: async (force: boolean = false) => {
        if (!get().loaded && get().loading && !force) {
          return undefined
        }

        const { currentUID, reviewToken, reviewPII } = useSessionStore.getState()
        if (!currentUID) {
          showErrorToast('No current UID set')
          return undefined
        }
        if (!reviewToken || !reviewPII) {
          // Not authenticated yet. Silent — the sign-in gate handles routing.
          return undefined
        }

        set(
          {
            loading: true,
            loaded: false,
            pendingReviews: undefined,
            reviewsDisabled: false,
            error: undefined,
          },
          false,
          'fetch/start'
        )

        let pendingReviews: PendingReviewsResponse | undefined
        try {
          const globalID = getCurrentGlobalID()
          const [result, error] = await withRefreshRetry((tok) =>
            getPendingReviews(currentUID, reviewPII, tok, {
              storeID: globalID ? getStoreIDByGlobalID(globalID) : undefined,
              menuType: getCurrentMenuType(),
            })
          )
          pendingReviews = result

          if (error) {
            logger.error('fetchPendingReviews', error)
            const cls = classifyReviewError(error)
            // A verified shopper with no imported purchases has no contact yet.
            // Keep the session and show the empty state instead of bouncing to sign-in.
            if (cls === 'contactNotFound') {
              set(
                { loading: false, loaded: true, pendingReviews: undefined, error: undefined },
                false,
                'fetch/noContact'
              )
              return undefined
            }
            set(
              {
                loading: false,
                loaded: true,
                pendingReviews: undefined,
                reviewsDisabled: cls === 'reviewsDisabled',
                error,
              },
              false,
              'fetch/error'
            )
            return undefined
          }

          // Hydrate BE-native `productId` to `product_uid` and provide safe
          // defaults for rec-only fields (THCA / cannabinoids) so downstream
          // consumers can treat pending products as regular `ProductView`s.
          const hydratedPending: PendingReviewsResponse | undefined = pendingReviews
            ? {
                ...pendingReviews,
                products: (pendingReviews.products ?? []).map(hydratePendingReviewProduct),
              }
            : undefined

          logger.log('fetchPendingReviews', { pendingReviews: hydratedPending })

          set(
            {
              loading: false,
              loaded: true,
              pendingReviews: hydratedPending,
              reviewsDisabled: false,
              error: undefined,
            },
            false,
            'fetch/success'
          )
          return hydratedPending
        } finally {
          track('review', 'list_ordered_products', {
            reviewable_size: (pendingReviews?.products ?? []).length,
          })
        }
      },

      initialize: async () => {
        await get().fetch()
      },

      reset: () => {
        set(initialState, false, 'reset')
      },
    }),
    { name: 'PendingReviewsStore', enabled: process.env.NODE_ENV === 'development' }
  )
)
